import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import { api, money, formatApiError } from "../api";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import { useAuth } from "../context/AuthContext";
import { buildOrderBookingBill } from "../utils/whatsappBill";

export default function Checkout() {
  const { cart, cartTotal, clearCart } = useCart();
  const { settings, whatsappLink } = useSettings();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    email: user?.email || "",
    address: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  if (!cart.length) {
    return (
      <main className="page">
        <div className="empty" data-testid="empty-checkout-message">Your cart is empty.</div>
      </main>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const items = cart.map((x) => ({
        product_id: x.product_id,
        name: x.name,
        look_number: x.look_number,
        image: x.image,
        price: x.price,
        size: x.size,
        color: x.color,
        quantity: x.quantity,
        category: x.category,
        subcategory: x.subcategory,
        customization: x.customization,
        delivery_time_days: x.delivery_time_days || 7,
      }));
      const { data } = await api.post("/api/orders", {
        customer_name: form.name,
        customer_phone: form.phone,
        customer_email: form.email || null,
        address: form.address,
        notes: form.notes || null,
        items,
      });
      const msg = buildOrderBookingBill({
        brandName: settings.brand_name,
        orderId: data.order_id,
        items: data.items || items,
        customer: {
          name: form.name,
          phone: form.phone,
          email: form.email,
          address: form.address,
        },
        notes: form.notes,
        total: data.total,
        now: data.created_at,
      });

      clearCart();
      const waUrl = whatsappLink(msg);
      try {
        window.open(waUrl, "_blank");
      } catch {}
      navigate(`/thank-you/${data.order_id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="page" data-testid="checkout-page">
      <div className="page-head">
        <span className="eyebrow">Final details</span>
        <h1>
          Make it <em>yours.</em>
        </h1>
        <p>We'll carry your order to WhatsApp for a personal confirmation.</p>
      </div>
      <form onSubmit={submit} className="checkout-form">
        <div className="form-field">
          <label>Full name</label>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="checkout-name-input" />
        </div>
        <div className="form-field">
          <label>Phone number</label>
          <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="checkout-phone-input" />
        </div>
        <div className="form-field">
          <label>Email (optional)</label>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="checkout-email-input" />
        </div>
        <div className="form-field">
          <label>Delivery address</label>
          <textarea required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="checkout-address-input" />
        </div>
        <div className="form-field">
          <label>Notes for the studio (optional)</label>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="checkout-notes-input" />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid var(--line)", paddingTop: 20, marginTop: 10 }}>
          <span className="eyebrow">Total to confirm</span>
          <strong style={{ fontFamily: "'Fraunces',serif", fontSize: 26 }} data-testid="checkout-total">{money(cartTotal)}</strong>
        </div>
        <label className="checkout-policy-consent">
          <input type="checkbox" required data-testid="checkout-policy-consent" />
          <span>
            I have reviewed and agree to the <Link to="/policies#terms" target="_blank">Terms & Conditions</Link>,{" "}
            <Link to="/policies#returns" target="_blank">Return & Refund Policy</Link> and{" "}
            <Link to="/policies#privacy" target="_blank">Privacy Policy</Link>.
          </span>
        </label>
        <button className="btn btn-primary btn-full" type="submit" disabled={submitting} data-testid="whatsapp-order-submit">
          {submitting ? "Sending…" : "Send order on WhatsApp"} <MessageCircle size={14} />
        </button>
      </form>
    </main>
  );
}
